import { useRef, useState, type ReactNode } from 'react';

import { Box, Card, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import ModalScreen from '@/designs-dev/device/ModalScreen';
import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AppBar from '@/karehero-app/shell/AppBar';
import {
  ACCOUNT_EMAIL,
  ACCOUNT_FACTS,
} from '@/karehero-app/access/AccountSheet.content';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';

// A tappable row in the sheet.
const Row = ({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick?: () => void;
}) => (
  <Card variant='option' onClick={onClick}>
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
      <Icon icon={icon as never} fontSize={18} />
      <Typography variant='h6'>{label}</Typography>
    </Stack>
  </Card>
);

/** Which of the sheet's two faces is showing. */
export type AccountView = 'menu' | 'details';

// What the avatar in the app bar opens. Sign out is the thing that matters
// before there is an account worth managing: a member who signed up on the
// wrong account has no other way back, and this is the whole reason the bar
// carries an avatar before we know their name.
//
// In the app it is the settings menu proper, because the avatar is the only
// control on every screen that is about them rather than about the person they
// care for: their own details are behind it, opening in place of the menu with
// a way back to it. The circle is not: it belongs to whichever person the bar
// names, so it hangs off the name. See CircleSwitcher.
const AccountSheet = ({
  // Every sheet names the account, not just the ones whose screen passes one:
  // it is how someone on the wrong account finds out.
  email = ACCOUNT_EMAIL,
  inApp = false,
  initialView = 'menu',
  initialEditingFact,
  onSignOut,
  onClose,
}: {
  /** The account they are signed in with, named so they can see it is wrong. */
  email?: string;
  /**
   * The fuller version, for the app rather than for getting into it.
   *
   * Before there is an account there is nothing to manage, so the sheet is the
   * way out and nothing else. Once they are in, the avatar is the only thing on
   * every screen that is about them rather than about the person they care for,
   * which makes it where their own details live.
   */
  inApp?: boolean;
  /** Harness-only: open straight onto one of the rows, for a flows page. */
  initialView?: AccountView;
  /** Harness-only: open Your details with one fact already in its editor. */
  initialEditingFact?: string;
  onSignOut?: () => void;
  onClose: () => void;
}) => {
  const [view, setView] = useState<AccountView>(initialView);
  // Where the sheet is going once it has finished sliding down. The sheet's own
  // close is what runs, so the menu leaves the way it arrived and the page
  // comes up after it rather than over it: the sheet outranks a full-screen
  // page, so unmounting it as the page mounts left it drawn on top for a frame.
  //
  // A ref, not state. The sheet holds the onClose it was handed when the row
  // was tapped and calls it when the animation ends, so a re-render in between
  // does not reach it: as state this read null a quarter of a second later and
  // closed the lot instead of opening anything.
  const going = useRef<AccountView | null>(null);
  const leaveFor = (next: AccountView, close: () => void) => {
    going.current = next;
    close();
  };
  // Back to the menu, and not still on its way anywhere: the X on the menu
  // closes the lot, which it cannot do while the sheet still thinks it is
  // leaving for a page.
  const back = () => {
    going.current = null;
    setView('menu');
  };

  /**
   * A row of the menu, as a page over the app rather than as a second sheet.
   *
   * Both of these open sheets of their own — a fact opens its editor, a member
   * opens their permissions — and a sheet stacked on a sheet leaves the first
   * one's header behind the second, so the screen ends up with two headings and
   * nothing to say which one you are under. The page takes the whole screen, so
   * the sheets it opens have somewhere to come up from.
   *
   * Back goes to the menu, because that is where it was opened from.
   */
  const page = (title: string, body: ReactNode) => (
    <ModalScreen from='right' onClose={back}>
      {(close) => (
        <ScreenRoot>
          <StatusBar />
          {/* Left aligned: the centred bar puts its title in h2, which on a
              phone is a heading that does not fit on one line. Left is h4
              beside the chevron, and it lines up with the bar the page was
              opened from. */}
          <AppBar title={title} align='left' onBack={close} />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, py: 2 }}>
            {body}
          </Box>
        </ScreenRoot>
      )}
    </ModalScreen>
  );

  // Their own details, not the care record's. Same list and same editor as the
  // profile, so a name is changed the same way wherever it is changed.
  if (view === 'details')
    return page(
      'Your details',
      <FactList
        groups={ACCOUNT_FACTS}
        edit='drawer'
        save='explicit'
        initialEditing={initialEditingFact}
      />,
    );

  return (
    <BottomSheet
      title='Settings'
      variant='hug'
      onClose={() => (going.current ? setView(going.current) : onClose())}
    >
      {(close) => (
        // Tight. The rows are a menu rather than a set of choices to weigh up,
        // and at the wider gap four of them filled a sheet.
        <Stack sx={{ pb: 2 }} spacing={1}>
          {inApp && (
            <>
              <Row
                icon='user'
                label='Your details'
                onClick={() => leaveFor('details', close)}
              />
            </>
          )}
          <Row
            icon='arrow-right-from-bracket'
            label='Sign out'
            onClick={() => {
              onClose();
              onSignOut?.();
            }}
          />
          {email && (
            <Typography
              sx={{ textAlign: 'center', color: 'text.secondary' }}
              variant='caption'
            >
              You&rsquo;re currently signed in with {email}
            </Typography>
          )}
        </Stack>
      )}
    </BottomSheet>
  );
};

AccountSheet.displayName = 'AccountSheet';
export default AccountSheet;
